import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, ShieldAlert, ScanSearch, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  fetchPublicGeneratedReport,
  generateQrCodeDataUrl,
  type PublicReportVerificationRecord,
} from '../services/reportVerification';

type VerifyReportPageProps = {
  documentId?: string;
};

const fieldValue = (value?: string | null) => (value && value.trim() ? value : '—');

const VerifyReportPage: React.FC<VerifyReportPageProps> = ({ documentId }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<PublicReportVerificationRecord | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!documentId) {
        setRecord(null);
        setError('');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setQrDataUrl('');

      try {
        const data = await fetchPublicGeneratedReport(documentId);
        if (!cancelled) {
          const nextRecord = (data as PublicReportVerificationRecord) || null;
          setRecord(nextRecord);
          if (nextRecord?.verificationUrl) {
            const qr = await generateQrCodeDataUrl(nextRecord.verificationUrl);
            if (!cancelled) {
              setQrDataUrl(qr);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('verifyReportLoadError'));
          setRecord(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [documentId, t]);

  const status = useMemo(() => {
    if (!record) return 'missing';
    if (record.isRevoked || record.status !== 'valid') return 'invalid';
    return 'valid';
  }, [record]);

  const statusCopy = status === 'valid'
    ? t('verifyReportValidTitle')
    : status === 'invalid'
      ? t('verifyReportInvalidTitle')
      : t('verifyReportNotFoundTitle');

  const statusBadgeClass =
    status === 'valid'
      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
      : status === 'invalid'
        ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
        : 'bg-slate-500/15 text-slate-300 border-slate-500/30';

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_35%),linear-gradient(180deg,#020617_0%,#0f172a_45%,#111827_100%)] px-4 py-10 text-white">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        <div className="flex items-center gap-4 rounded-3xl border border-slate-700/70 bg-slate-950/70 px-6 py-5 shadow-2xl backdrop-blur">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-blue-500/20 bg-blue-500/10 text-blue-300">
            <ScanSearch className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-300/80">{t('verifyReportTitle')}</p>
            <h1 className="mt-1 text-2xl font-bold">{t('verifyReportSubtitle')}</h1>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-700/70 bg-slate-950/80 p-6 shadow-2xl backdrop-blur">
          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center">
              <div className="flex items-center gap-3 text-slate-300">
                <Loader2 className="h-5 w-5 animate-spin text-blue-400" />
                <span>{t('verifyReportLoading')}</span>
              </div>
            </div>
          ) : status === 'missing' ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full border border-slate-700 bg-slate-900 text-slate-400">
                <ShieldAlert className="h-9 w-9" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{t('verifyReportNotFoundTitle')}</h2>
                <p className="mt-2 max-w-lg text-sm text-slate-400">{t('verifyReportNotFoundBody')}</p>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[1.4fr_0.9fr]">
              <div className="space-y-4">
                <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${statusBadgeClass}`}>
                  <ShieldCheck className="h-4 w-4" />
                  {statusCopy}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportDocumentTypeLabel')}</p>
                    <p className="mt-2 text-sm font-semibold text-white">{fieldValue(record?.title)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportCodeLabel')}</p>
                    <p className="mt-2 font-mono text-sm font-semibold text-white">{fieldValue(record?.verificationCode)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportIssuedAtLabel')}</p>
                    <p className="mt-2 text-sm font-semibold text-white">{fieldValue(record?.issuedAtFormatted)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportStatusLabel')}</p>
                    <p className="mt-2 text-sm font-semibold text-white">{statusCopy}</p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportDoctorLabel')}</p>
                    <p className="mt-2 text-sm font-semibold text-white">{fieldValue(record?.doctorName)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportCrmLabel')}</p>
                    <p className="mt-2 text-sm font-semibold text-white">
                      {fieldValue(record?.doctorCrm)}{record?.doctorCrmState ? ` / ${record.doctorCrmState}` : ''}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportRqeLabel')}</p>
                    <p className="mt-2 text-sm font-semibold text-white">{fieldValue(record?.doctorRqe)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportSpecialtyLabel')}</p>
                    <p className="mt-2 text-sm font-semibold text-white">{fieldValue(record?.doctorSpecialty)}</p>
                  </div>
                  {record?.doctorSubspecialty ? (
                    <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportSubspecialtyLabel')}</p>
                      <p className="mt-2 text-sm font-semibold text-white">{fieldValue(record?.doctorSubspecialty)}</p>
                    </div>
                  ) : null}
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportOrthoIdLabel')}</p>
                    <p className="mt-2 text-sm font-semibold text-white">{fieldValue(record?.orthoId)}</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-700/80 bg-slate-900/60 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('verifyReportPatientLabel')}</p>
                  <p className="mt-2 text-sm font-semibold text-white">{fieldValue(record?.patientNameMasked)}</p>
                  {record?.anatomy ? (
                    <p className="mt-2 text-xs text-slate-400">{record.anatomy}</p>
                  ) : null}
                </div>

                {error ? (
                  <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
                    {error}
                  </div>
                ) : null}
              </div>

              <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-slate-700/80 bg-slate-900/70 p-6 text-center">
                <div className="rounded-2xl bg-white p-3 shadow-lg">
                  {qrDataUrl ? (
                    <img
                      src={qrDataUrl}
                      alt={t('verifyReportQrAlt')}
                      className="h-56 w-56"
                    />
                  ) : null}
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-300">{t('verifyReportPortalLabel')}</p>
                  <p className="mt-2 text-sm text-slate-300">{t('verifyReportScanQr')}</p>
                  <p className="mt-3 break-all font-mono text-xs text-slate-400">
                    {record?.verificationUrl || '—'}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 px-4 py-3 text-xs leading-5 text-slate-400">
                  {t('verifyReportSignedBy')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyReportPage;

